import type { IsoDate } from '../../domain/dates';
import type { AppData, StoredPlan } from './app-data';
import {
  runPlanJob,
  type PlanAnswerMessage,
  type PlanJob,
  type PlanRequestMessage,
} from './plan-jobs';
import type { EditResult, PlanEdit, createPlan } from './plan-service';

/**
 * DE: Was der Client vom Web Worker braucht (im Test ersetzbar).
 * EN: What the client needs from the web worker (replaceable in tests).
 */
export interface PlanWorker {
  postMessage: (message: PlanRequestMessage) => void;
  addEventListener: (type: 'message', handler: (event: MessageEvent) => void) => void;
}

export interface PlanClient {
  createPlan: (data: AppData, options: Parameters<typeof createPlan>[1]) => Promise<StoredPlan>;
  editPlan: (data: AppData, today: IsoDate, edit: PlanEdit) => Promise<EditResult>;
}

type Pending = { resolve: (result: EditResult) => void; reject: (error: Error) => void };

// DE: Ohne Worker erst nach dem nächsten Zeichnen rechnen, damit „Plan wird erstellt …“ sichtbar wird.
// EN: Without a worker compute after the next paint, so "Planning…" becomes visible.
const runOnPage = (job: PlanJob) =>
  new Promise<EditResult>((resolve, reject) => {
    setTimeout(() => {
      try {
        resolve(runPlanJob(job));
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    }, 0);
  });

/**
 * DE: Plan-Berechnungen asynchron, im Web Worker wenn vorhanden, sonst auf der Seite.
 * EN: Plan calculations asynchronously, in the web worker when there is one, else on the page.
 */
export function createPlanClient(makeWorker: (() => PlanWorker) | undefined): PlanClient {
  const pending = new Map<number, Pending>();
  let worker: PlanWorker | undefined;
  let nextId = 0;
  const answer = (event: MessageEvent) => {
    const message = event.data as PlanAnswerMessage;
    const waiting = pending.get(message.id);
    pending.delete(message.id);
    if ('error' in message) waiting?.reject(new Error(message.error));
    else waiting?.resolve(message.result);
  };
  const run = (job: PlanJob): Promise<EditResult> => {
    if (!makeWorker) return runOnPage(job);
    if (!worker) {
      worker = makeWorker();
      worker.addEventListener('message', answer);
    }
    const id = nextId++;
    const started = worker;
    return new Promise((resolve, reject) => {
      pending.set(id, { resolve, reject });
      started.postMessage({ id, job });
    });
  };
  return {
    createPlan: async (data, options) => (await run({ kind: 'create', data, options })).stored,
    editPlan: (data, today, edit) => run({ kind: 'edit', data, today, edit }),
  };
}

let shared: PlanClient | undefined;

/**
 * DE: Gemeinsamer Client der App; der Worker startet erst beim ersten Auftrag.
 * EN: The app's shared client; the worker starts only with the first job.
 */
export function planClient(): PlanClient {
  shared ??= createPlanClient(
    typeof Worker === 'undefined'
      ? undefined
      : () => new Worker(new URL('./plan-worker.ts', import.meta.url), { type: 'module' }),
  );
  return shared;
}
